import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { getNotifications } from '../services/api';
import { Text } from './ui';
import RecipeQuotaBadge from './RecipeQuotaBadge';
import PremiumFeatures from './PremiumFeatures';
import QuestionMarkIcon from './QuestionMarkIcon';
import EducationModal from './EducationModal';

const UserButtons = () => {
    const { user, logout } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [showProfileName, setShowProfileName] = useState(true);
    
    // Education modal states
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    // Check if screen is mobile width and profile name length
    useEffect(() => {
        const checkMobile = () => {
            const isMobileWidth = window.innerWidth < 768; // Tailwind's md breakpoint
            setIsMobile(isMobileWidth);
        };

        const checkProfileName = () => {
            if (!user) return;
            
            const profileName = user.displayName || 'User';
            const isLongName = profileName.length > 12; // Threshold for long names
            const isSmallScreen = window.innerWidth < 1024; // lg breakpoint
            
            // Hide profile name if it's long AND screen is small, or if on mobile
            setShowProfileName(!isMobile && !(isLongName && isSmallScreen));
        };

        // Check on mount
        checkMobile();
        checkProfileName();

        // Listen for resize events
        window.addEventListener('resize', () => {
            checkMobile();
            checkProfileName();
        });
        
        return () => window.removeEventListener('resize', () => {
            checkMobile();
            checkProfileName();
        });
    }, [user, isMobile]);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    // Fetch unread notification count
    useEffect(() => {
        // Only fetch if user has a profile (is registered in database)
        if (user && user.profile) {
            fetchUnreadCount();
            // Set up polling for unread count
            const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds
            return () => clearInterval(interval);
        }
    }, [user, user?.profile]);

    const fetchUnreadCount = async () => {
        try {
            // Only fetch if user has a profile (is registered in database)
            if (!user?.profile) {
                return;
            }
            
            const token = await user.getIdToken();
            const data = await getNotifications({ unread_only: true, limit: 1 }, token);
            setUnreadCount(data.unread_count);
        } catch (error) {
            // Silently handle USER_NOT_REGISTERED errors - user might still be registering
            if (error.code === 'USER_NOT_REGISTERED' || error.requiresRegistration) {
                // User is not registered yet, just skip for now
                return;
            }
            console.error('Error fetching notification count:', error);
        }
    };

    if (!user) {
        return (
            <div className="flex gap-4">
                <Link 
                    to="/login" 
                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity duration-120"
                >
                    Login
                </Link>
                {/* TEMP DISABLED - invite only
                <Link 
                    to="/register" 
                    className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity duration-120"
                >
                    Register
                </Link>
                */}
            </div>
        );
    }

    return (
        <>
        <div className="flex items-center gap-4 pb-2">
            {/* Recipe Quota Badge - Only for free users and on non-mobile screens */}
            {!isMobile && <RecipeQuotaBadge />}
            
            
            {/* Notifications Link */}
            <div className="relative group">
                <Link 
                    to="/notifications" 
                    className="relative font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity duration-120"
                    title="Notifications"
                >
                    <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="w-4 h-4"
                    >
                        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
                        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
                    </svg>
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-mono">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Link>
                <QuestionMarkIcon 
                    onOpenModal={() => setShowNotificationsModal(true)} 
                    onCloseModal={() => setShowNotificationsModal(false)}
                    isModalOpen={showNotificationsModal}
                />
            </div>
            
            {/* Cooking Activity Link */}
            <div className="relative group">
                <Link 
                    to="/activity" 
                    className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity duration-120"
                    title="Cooking Activity"
                >
                    <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="w-4 h-4"
                    >
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12,6 12,12 16,14"/>
                    </svg>
                </Link>
                <QuestionMarkIcon 
                    onOpenModal={() => setShowActivityModal(true)} 
                    onCloseModal={() => setShowActivityModal(false)}
                    isModalOpen={showActivityModal}
                />
            </div>
            
            {/* Profile Link */}
            <div className="relative group">
                <Link 
                    to={`/profile/${user.uid}`} 
                    className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity duration-120"
                    title={showProfileName ? undefined : (user.displayName || 'User')}
                >
                    {showProfileName ? (
                        user.displayName || 'User'
                    ) : (
                        <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="w-4 h-4"
                        >
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                        </svg>
                    )}
                </Link>
                <QuestionMarkIcon 
                    onOpenModal={() => setShowProfileModal(true)} 
                    onCloseModal={() => setShowProfileModal(false)}
                    isModalOpen={showProfileModal}
                />
            </div>
            
            {/* Logout Button */}
            <div className="relative group">
                <button 
                    onClick={handleLogout} 
                    className="px-3 py-2 hairline bg-neutral-0 text-neutral-900 font-mono text-xs uppercase tracking-wider opacity-60 hover:opacity-100 transition-opacity duration-120"
                >
                    Logout
                </button>
                <QuestionMarkIcon 
                    onOpenModal={() => setShowLogoutModal(true)} 
                    onCloseModal={() => setShowLogoutModal(false)}
                    isModalOpen={showLogoutModal}
                />
            </div>
        </div>
        <>
            {/* Education Modals for User Buttons */}
            <EducationModal
                isOpen={showNotificationsModal}
                onClose={() => setShowNotificationsModal(false)}
                title="Notifications"
                description="Stay updated with activity on your recipes. Receive notifications for merge proposals, friend requests, and other important updates."
                image="/images/education/notifications-demo.png"
                details={[
                    "Get notified when someone wants to merge changes to your recipes",
                    "Receive friend requests and connection updates",
                    "See when your merge proposals are approved or rejected",
                    "Stay informed about recipe updates and changes",
                    "Manage notification preferences in settings"
                ]}
            />
            <EducationModal
                isOpen={showActivityModal}
                onClose={() => setShowActivityModal(false)}
                title="Cooking Activity"
                description="View your cooking history and session data. See all your past cooking sessions, timers, and recipe activity in one place."
                image="/images/education/activity-demo.png"
                details={[
                    "View all your past cooking sessions",
                    "See session details, timers, and duration",
                    "Access session reflections and notes",
                    "Resume paused sessions",
                    "Track your cooking history over time"
                ]}
            />
            <EducationModal
                isOpen={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                title="Profile"
                description="Manage your account settings, view your profile, and customize your cooking experience. Update your preferences and account information."
                image="/images/education/profile-demo.png"
                details={[
                    "View and edit your profile information",
                    "Manage account settings and preferences",
                    "Update subscription and billing information",
                    "View your recipe statistics and activity",
                    "Customize your cooking experience"
                ]}
            />
            <EducationModal
                isOpen={showLogoutModal}
                onClose={() => setShowLogoutModal(false)}
                title="Logout"
                description="Sign out of your account. You can log back in at any time to access your recipes and cooking data."
                image="/images/education/logout-demo.png"
                details={[
                    "Securely sign out of your account",
                    "Your data is saved and will be available when you log back in",
                    "You can log in again at any time",
                    "All your recipes and sessions are preserved"
                ]}
            />
        </>
        </>
    );
};

export default UserButtons; 